import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AdminSettings } from "@/types/adminSettings";

interface ConsoleLogsTabProps {
  settings: AdminSettings;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
}

export const ConsoleLogsTab = ({
  settings,
  updateSettings,
  saveSettings,
  isSaving
}: ConsoleLogsTabProps) => {
  const consoleLogs = settings.consoleLogs || {
    indexPage: {
      componentRendering: true,
      authFlow: true,
      networkStatus: true,
      termsCheck: true,
      errorHandling: true,
      performanceMetrics: true
    },
    performance: {
      performanceMonitor: false,
      fontLoading: false,
    },
    auth: {
      authContext: false,
    },
    admin: {
      useAdminSettings: false,
    },
    debugging: {
      enhancedLogging: false,
      logFunctionDuration: false,
    },
    diagnostics: {
      iconMapping: false,
      imageTransform: false,
    }
  };

  const handleToggle = (section: string, key: string, value: boolean) => {
    updateSettings(['consoleLogs', section, key], value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Console Logging Controls</h3>
        <p className="text-sm text-gray-600 mb-6">
          Control what gets logged to the console to reduce performance overhead and console clutter.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Index Page Logging</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="component-rendering" className="text-sm">
                Component Rendering
              </Label>
              <Switch
                id="component-rendering"
                checked={consoleLogs.indexPage?.componentRendering || false}
                onCheckedChange={(checked) => 
                  handleToggle('indexPage', 'componentRendering', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="auth-flow" className="text-sm">
                Authentication Flow
              </Label>
              <Switch
                id="auth-flow"
                checked={consoleLogs.indexPage?.authFlow || false}
                onCheckedChange={(checked) => 
                  handleToggle('indexPage', 'authFlow', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="network-status" className="text-sm">
                Network Status
              </Label>
              <Switch
                id="network-status"
                checked={consoleLogs.indexPage?.networkStatus || false}
                onCheckedChange={(checked) => 
                  handleToggle('indexPage', 'networkStatus', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="terms-check" className="text-sm">
                Terms of Service Check
              </Label>
              <Switch
                id="terms-check"
                checked={consoleLogs.indexPage?.termsCheck || false}
                onCheckedChange={(checked) => 
                  handleToggle('indexPage', 'termsCheck', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="error-handling" className="text-sm">
                Error Handling
              </Label>
              <Switch
                id="error-handling"
                checked={consoleLogs.indexPage?.errorHandling || false}
                onCheckedChange={(checked) => 
                  handleToggle('indexPage', 'errorHandling', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="performance-metrics" className="text-sm">
                Performance Metrics
              </Label>
              <Switch
                id="performance-metrics"
                checked={consoleLogs.indexPage?.performanceMetrics || false}
                onCheckedChange={(checked) => 
                  handleToggle('indexPage', 'performanceMetrics', checked)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Performance Logging</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="performance-monitor" className="text-sm">
                Performance Monitor
              </Label>
              <Switch
                id="performance-monitor"
                checked={consoleLogs.performance?.performanceMonitor || false}
                onCheckedChange={(checked) => 
                  handleToggle('performance', 'performanceMonitor', checked)
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="font-loading" className="text-sm">
                Font Loading Monitor
              </Label>
              <Switch
                id="font-loading"
                checked={consoleLogs.performance?.fontLoading || false}
                onCheckedChange={(checked) => 
                  handleToggle('performance', 'fontLoading', checked)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Core Logic Logging</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="auth-context" className="text-sm">
                Auth Context
              </Label>
              <Switch
                id="auth-context"
                checked={consoleLogs.auth?.authContext || false}
                onCheckedChange={(checked) => 
                  handleToggle('auth', 'authContext', checked)
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="admin-settings-hook" className="text-sm">
                Admin Settings Hook
              </Label>
              <Switch
                id="admin-settings-hook"
                checked={consoleLogs.admin?.useAdminSettings || false}
                onCheckedChange={(checked) => 
                  handleToggle('admin', 'useAdminSettings', checked)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Debugging Utilities</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="enhanced-logging" className="text-sm">
                Enhanced Logging
              </Label>
              <Switch
                id="enhanced-logging"
                checked={consoleLogs.debugging?.enhancedLogging || false}
                onCheckedChange={(checked) => 
                  handleToggle('debugging', 'enhancedLogging', checked)
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="log-function-duration" className="text-sm">
                Log Function Duration
              </Label>
              <Switch
                id="log-function-duration"
                checked={consoleLogs.debugging?.logFunctionDuration || false}
                onCheckedChange={(checked) => 
                  handleToggle('debugging', 'logFunctionDuration', checked)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Diagnostic Logging</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="icon-mapping-logging" className="text-sm">
                Icon Mapping
              </Label>
              <Switch
                id="icon-mapping-logging"
                checked={consoleLogs.diagnostics?.iconMapping || false}
                onCheckedChange={(checked) => 
                  handleToggle('diagnostics', 'iconMapping', checked)
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="image-transform-logging" className="text-sm">
                Image Transform Monitor
              </Label>
              <Switch
                id="image-transform-logging"
                checked={consoleLogs.diagnostics?.imageTransform || false}
                onCheckedChange={(checked) => 
                  handleToggle('diagnostics', 'imageTransform', checked)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="pt-4 border-t">
        <Button 
          onClick={saveSettings}
          disabled={isSaving}
          className="w-full md:w-auto"
        >
          {isSaving ? "Saving..." : "Save Console Log Settings"}
        </Button>
      </div>
    </div>
  );
};
